import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Set or replace the password on a passenger account, proven by the private link they arrived on. */
export const RiderPassword = ({ token, hasPassword, canSet = true, onSet }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const save = async () => {
    setBusy(true); setError("");
    try {
      await api.put(`/public/rider-auth/session/${token}/password`, { password });
      setDone(true); setPassword(""); onSet?.();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="rider-password-panel" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
        <KeyRound className="h-3 w-3" /> {hasPassword ? "Change your password" : "Set a password"}
      </p>
      <p className="mt-2 text-xs text-slate-500">
        {!canSet
          ? "For your security, passwords can only be set from a link you have just requested. Ask for a fresh sign-in link on the booking page and set it from that email."
          : hasPassword
            ? "Pick a new password if you'd like to change it."
            : "Choose a password and you can sign straight in next time instead of waiting for an email."}
      </p>
      <div className={`mt-4 flex flex-col gap-2 sm:flex-row ${canSet ? "" : "hidden"}`}>
        <Input data-testid="rider-password-input" type="password" autoComplete="new-password"
          placeholder="New password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)}
          className="h-12 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
        <Button data-testid="rider-password-save" disabled={busy || password.length < 8} onClick={save}
          className="h-12 shrink-0 bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save password"}
        </Button>
      </div>
      {done && <p data-testid="rider-password-done" className="mt-3 text-sm text-emerald-400">Password saved — sign in with it any time.</p>}
      {error && <p data-testid="rider-password-error" className="mt-3 text-sm text-red-400">{error}</p>}
    </div>
  );
};
